import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  clientViewAttachment,
  clientRemoveAttachment,
} from '../../../../services/ticketDetails/users';
import { ticketQueryKeys } from './useTicketQueries';
import { uploadAttachment, uploadDirectlyToS3 } from '../../../../services/general';

const IMAGE_TYPE_PREFIX = 'image/';
const PDF_TYPE = 'application/pdf';

export const isImageFile = (file) => file.type.startsWith(IMAGE_TYPE_PREFIX);

const isPdfFile = (file) =>
  file.type === PDF_TYPE || (!file.type && file.name?.toLowerCase().endsWith('.pdf'));

export const isAllowedAttachment = (file) => isImageFile(file) || isPdfFile(file);

export function useUploadAttachment({ onSuccess, onError } = {}) {
  return useMutation({
    mutationFn: async ({ file, folderName, rootFolder, onUploadProgress }) => {
      if (!isAllowedAttachment(file)) {
        throw new Error('Only image and PDF files can be attached.');
      }

      // S3 policy matches Content-Type exactly, so always send a real type.
      const contentType = file.type || PDF_TYPE;

      const { documentId, uploadUrl, uploadFields } = await uploadAttachment({
        fileName: file.name,
        fileSize: file.size,
        contentType,
        folderName,
        rootFolder,
      });

      const formData = new FormData();
      Object.entries(uploadFields).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file);

      await uploadDirectlyToS3(uploadUrl, formData, onUploadProgress);

      return {
        documentId,
        name: file.name,
        fileSize: file.size,
        applicationType: contentType,
      };
    },
    onSuccess,
    onError,
  });
}

export function useViewAttachment({ onError } = {}) {
  return useMutation({
    mutationFn: clientViewAttachment,
    onSuccess: (data) => {
      const url = data?.documentUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    onError,
  });
}

export function useRemoveAttachment({ onSuccess, onError } = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clientRemoveAttachment,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ticketQueryKeys.list(variables.ticketId) });
      onSuccess?.(data, variables);
    },
    onError,
  });
}
